import api from '@/api';
import Logger from '@/logger';
import router from '../../../router';
import { notifyError, notifySuccess } from '../../utils';

const { colors } = require('./colors.json')
const logger = new Logger('store/matching/actions')


/**
 * Create an index to have a searcher for the given brand and classificationId.
 * @param {Object} context
 * @param {Object} payload
 * @param {String} payload.brand with the brand for the index.
 * @param {Number} payload.classificationId with the id of the classification for the index.false
 */
function createIndex({ commit }, { brand, classificationId }) {
  if (!brand) throw new Error('Please provide the "brand"')
  if (!classificationId) throw new Error('Please provide the "classificationId"')

  commit('setLoadingIndexCreation', true)
  return api.similar.createSearcher({ brand, classificationId })
    .then(() => {
      notifySuccess(commit, `Comenzó la indexación para la marca "${brand}" de la clasificación ${classificationId}, esto puede tardar varios minutos en verse reflejado en la aplicación`)
    })
    .catch(err => {
      logger.error(err);
      notifyError(commit, 'No pudimos comenzar la creación del índice')
    })
    .then(() => commit('setLoadingIndexCreation', false))
}


/**
 * Create the QMatchProduct with the selected items and the pivot then
 * get again the pivot items and set a new pivot.
 * @param {Object} context
 */
function createProduct({ commit, state, getters, dispatch }, { brand, classificationId }) {
  if (!getters.pivotItem) throw new Error('There is no pivot item')
  if (!classificationId) throw new Error('Please provide the "classificationId"')
  if (!brand) throw new Error('Please provide the "brand"')

  // Here we get the pivot and selected items
  let itemsToMatch = [getters.pivotItem, ...state.selectedItems]
  // We get the sellerId and historyId of erveryone
  let items = itemsToMatch.map(item => ({
    sellerId: item.sellerId,
    historyId: item.historyId,
    created: item.created,
    creatorUser: item.creatorUser,
    productId: item.productId
  }))
  // Finally we get the grouped items that had same historyId but different sellerId
  // Those items are grouped based on the sellerHomologations variable from the matching state
  itemsToMatch.forEach(item => {
    if (!item.homologedItems) return
    const homologedItems = item.homologedItems.map(item => ({
      sellerId: item.sellerId,
      historyId: item.historyId,
      created: item.created,
      creatorUser: item.creatorUser,
      productId: item.productId
    }))
    items = items.concat(...homologedItems)
  })

  commit('setLoadingProductCreation', true)
  return api.similar.createProduct({ classificationId, items })
    .then(() => {
      notifySuccess(commit, 'QMatchProduct creado con éxito')
      commit('clearSelectedItems')
      dispatch('getPivotItems', { brand, classificationId, page: 1 })
    })
    .catch(err => {
      logger.error(err)
      notifyError(commit, 'No pudimos crear el QMatchProduct')
    })
    .then(() => commit('setLoadingProductCreation', false))
}


/**
 * Create a QMP with a single item given.
 * This action won't change the pivot it only will reload the similar items
 * to get again the items with their corresponding new productId.
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} payload.classificationId with the id of the classification to set in the QMP.
 * @param {Object} payload.item
 * @param {Number} payload.item.sellerId
 * @param {String} payload.item.historyId
 * @param {Boolean} [payload.getSimilarItemsAgain] Default `true`. Indicates if the action must get
 * the similar items again or not.
 */
function createProductWithSingleItem({ commit, dispatch, getters }, { brand, classificationId, item, getSimilarItemsAgain = true }) {
  if (!item || !item.sellerId || !item.historyId) {
    throw new Error('Please provide an item with "sellerId" and "historyId"')
  }

  commit('setLoadingProductCreation', true)
  return api.similar.createProduct({ classificationId, items: [item] })
    .then(() => {
      notifySuccess(commit, 'QMP creado con éxito')
      const { sellerId, historyId } = getters.pivotItem
      if (getSimilarItemsAgain) {
        dispatch('getSimilarItems', { brand, classificationId, page: 1, sellerId, historyId })
      }
    })
    .catch(err => {
      logger.error(err)
      notifyError(commit, 'No pudimos crear el QMP')
    })
    .then(() => commit('setLoadingProductCreation', false))
}


/**
 * Dispatch all the actions that depends on values of the route.
 *
 * @param {Object} context
 */
async function getAllDataAccordingToRoute({ dispatch }) {
  /* Get the classifications again */
  await dispatch('getClassifications')

  /* Check if we have a selected classification and get again the brands */
  const classificationId = router.currentRoute.params.classificationId
  if (classificationId) await dispatch('getBrands')

  /* Check if we have a selected brand to get the pivots and the similar items again */
  const brand = router.currentRoute.params.brandName
  if (brand) {
    await dispatch('getPivotItems', { page: 1 })
    await dispatch('getSimilarItems')
  }
}


/**
 * Get the brands of the given classification and populate the store with them. 
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} payload.classificationId to get the brands of this classification.
 */
function getBrands({ commit, state }, { classificationId } = {}) {
  if (!classificationId) {
    classificationId = router.currentRoute.params.classificationId;
    if (!classificationId) throw new Error('Please provide the "classificationId"');
  }

  commit('setLoadingBrands', true)
  return api.matching.brands.get({ classificationId, ...state.appSettings })
    .then(brands => commit('setBrands', brands))
    .catch(err => {
      logger.error(err);
      notifyError('No pudimos cargar las marcas')
    })
    .then(() => commit('setLoadingBrands', false))
}


/**
 * Get the classifications according the their pagination object.
 * @param {Object} context
 * @param {Number} page 1-indexed.
 */
function getClassifications({ commit, state }) {
  /* Avoid calling again the action that is already running */
  if (state.loadingClassifications) return;

  /* Expect that at least one client seller id is set */
  if (!state.appSettings.clientsSellersIds.length) {
    return notifyError(commit, 'Por favor selecciona al menos un seller cliente en las configuraciones')
  }

  commit('setLoadingClassifications', true)
  return api.matching.classifications.get({ ...state.appSettings })
    .then(classifications => commit('setClassifications', classifications))
    .catch(err => {
      logger.error(err)
      notifyError(commit, 'No se pudieron cargar las clasificaciones')
    })
    .then(() => commit('setLoadingClassifications', false))
}


/**
 * Get the QMIs to use as pivot to find their matches.
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} [payload.page] 1-indexed
 * @param {Number} payload.classificationId
 */
function getPivotItems({ commit, state }, { brand, classificationId, page } = {}) {
  if (!classificationId) {
    classificationId = router.currentRoute.params.classificationId
    if (!classificationId) throw new Error('Please provide the "classificationId"')
  }

  if (!brand) {
    brand = router.currentRoute.params.brandName
    if (!brand) throw new Error('Please provide the "brand"')
  }

  const fetch = state.pivotItemsFetch
  const offset = page ? (page - 1) * fetch : state.pivotItemsOffset
  commit('setLoadingPivotItems', true)
  return api.similar.getPivots({
    brand,
    classificationId,
    fetch,
    offset,
    ...state.appSettings,
    sellersIds: [...state.appSettings.clientsSellersIds, ...state.appSettings.competitorsSellersIds]
  })
    .then(({ offset, fetch, total, items }) => {
      commit('setPivotItems', items)
      commit('setPivotItemsFetch', fetch)
      commit('setPivotItemsOffset', offset)
      commit('setPivotItemsTotal', total)
    })
    .catch(err => {
      logger.error(err)
      notifyError(commit, 'No se pudieron cargar los items pivotes')
    })
    .then(() => commit('setLoadingPivotItems', false))
}


/**
 * Get the page of QMPs that contains items with the given classification id and brand.
 * @param {Object} context
 * @param {Object} payload
 * @param {String} payload.brand
 * @param {Number} payload.classificationId
 * @param {Number} [payload.page]
 */
function getQmps({ commit, state }, { brand, classificationId, page }) {
  if (!brand) throw new Error('Please provide the "brand"')
  if (!classificationId) throw new Error('Please provide the "classificationId"')

  const historyIds = state.appSettings.historyIds
  const matchingBrands = state.appSettings.matchingBrands
  const fetch = state.qmpsFetch
  const offset = page ? (page - 1) * fetch : state.qmpsOffset
  commit('setLoadingQmps', true)
  commit('setQmps', [])
  return api.similar.getQmps({ brand, classificationId, fetch, offset, historyIds, matchingBrands })
    .then(({ fetch, offset, total, qmps }) => {
      commit('setQmps', qmps)
      commit('setQmpsFetch', fetch)
      commit('setQmpsOffset', offset)
      commit('setQmpsTotal', total)
    })
    .catch(err => {
      logger.error(err)
      notifyError(commit, 'No se pudieron cargar los QMPs')
    })
    .then(() => commit('setLoadingQmps', false))
}

async function getMatchRanking({ commit }) {
  commit("loadingMatchRanking", true);
  return api.similar
    .getMatchRanking()
    .then((ranking) => {
      commit("matchRanking", ranking);
      commit("loadingMatchRanking", false);
    })
    .catch((error) => {
      commit("loadingMatchRanking", false);
      throw error;
    });
}
/**
 * Get the similar items to the given pivot.
 * 
 * If no pivot is given it will try one from the getter `pivotItem`.
 * If no brand or no classification id is given it will try to get them from the route.
 *
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} [payload.page]
 * @param {Number} [payload.sellerId]
 * @param {String} [payload.historyId]
 * @param {Number} [payload.classificationId]
 */
function getSimilarItems({ commit, dispatch, getters, state }, { brand, classificationId, page = 1, sellerId, historyId } = {}) {
  /* Do nothing if we are already getting the items */
  const pivotItem = getters.pivotItem

  if (state.loadingSimilarItems) return;

  /* Get the sellerId and historyId from the pivot if no one is given */
  if (!sellerId || !historyId) {
    if (!pivotItem) throw new Error('No "sellerId" or "historyId" and no current "pivotItem"')
    sellerId = pivotItem.sellerId;
    historyId = pivotItem.historyId;
  }

  /* Get the classification id from the route params */
  if (!classificationId) {
    classificationId = router.currentRoute.params.classificationId;
    if (!classificationId) throw new Error('Please provide the "classificationId"')
  }

  /* Get the brand from the route params */
  if (!brand) {
    brand = router.currentRoute.params.brandName;
    if (!brand) throw new Error('Please provide the "brand"')
  }

  /* Generate the params for the api call */
  const fetch = state.similarItemsFetch
  const vector = pivotItem.textEmbedding
  const offset = page ? (page - 1) * fetch : state.similarItemsOffset
  const orderBy = state.similarItemsOrderBy
  const brands = [brand]
  const classificationsIds = [classificationId]
  const image = state.pivotItems[0] && state.pivotItems[0].image || '';

  /* Get the similar items */
  commit('setLoadingSimilarItems', true)
  commit('setSimilarItems', [])
  return api.similar.getSimilar({
    brands,
    classificationsIds,
    fetch,
    image,
    offset,
    sellerId,
    historyId,
    orderBy,
    ...state.similarItemsFilters,
    ...state.appSettings,
    sellersIds: undefined, // Do not filter by the app settings sellers ids

  }, vector)
    .then(({ offset, fetch, total, orderBy, items }) => {
      commit('setSimilarItems', items)
      commit('setSimilarItemsFetch', fetch)
      commit('setSimilarItemsOffset', offset)
      commit('setSimilarItemsTotal', total)
      commit('setSimilarItemsOrderBy', orderBy)
      dispatch('getSimilarItemsQmps')
    })
    .catch(err => {
      logger.error(err);

      /* There is no index for the given classification and brand */
      if (err.response.status === 412) {
        return notifyError(commit, `No existe índice para la clasificación ${classificationId} y marca ${brand}`)
      }

      /* The item was not found in the index probably because is outdated */
      if (err.response.status === 404) {
        return notifyError(commit, `Pivote (historyId ${historyId}, sellerId ${sellerId}) no existe en el índice, intenta actualizarlo.`)
      }

      notifyError(commit, err.response.data)
    })
    .then(() => commit('setLoadingSimilarItems', false))
}


/**
 * Get the QMPs of the similar items present in the store.
 * 
 * Very useful to get the items that are match with the a given one. For example, you could load
 * a page of similar items, what happen if a match of any of those items is not in the same page?
 * We need to get it to make the full match with all QMIs from the same QMP.
 * 
 * @param {Object} context
 */
function getSimilarItemsQmps({ commit, state }) {
  const items = [...state.similarItems, ...state.selectedItems].filter(item => !!item.productId)

  /* Do nothing if there is no similar item */
  if (!items.length) return

  /* Get the unique ids of the QMPs */
  const ids = []
  items.forEach(item => {
    if (!ids.some(id => id === item.productId)) {
      ids.push(item.productId)
    }
  })

  /* Get the QMPS */
  commit('setLoadingSimilarItemsQmps', true)
  return Promise.all(ids.map(id => api.similar.getQmp({ id })))
    .then(qmps => {
      /* Assign a color to each QMP and set the object with the keyed qmps */
      const keyed = {}
      qmps.forEach((qmp, index) => { keyed[qmp.id] = { ...qmp, color: colors[index % colors.length] } })
      commit('setSimilarItemsQmps', keyed)
    })
    .catch(err => {
      logger.error(err);
      notifyError(commit, 'No pudimos cargar los QMPs de los items similares')
    })
    .then(() => commit('setLoadingSimilarItemsQmps', false))
}


/**
 * Remove a QMI from a given QMP.
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} payload.qmpId
 * @param {Number} payload.sellerId
 * @param {String} payload.historyId
 */
function removeQmiFromQmp({ commit, state }, { qmpId, sellerId, historyId }) {
  if (!qmpId) throw new Error('Please provide the "qmpId"')
  if (!sellerId || !historyId) throw new Error('Please provide the "sellerId" and "historyId"')

  commit('setLoadingRemoveQmiFromQmp', true)
  return api.similar.removeQmiFromQmp({ qmpId, sellerId, historyId })
    .then(() => {
      notifySuccess(commit, 'QMI eliminado correctamente del QMP')
      const qmps = [...state.qmps]
      const qmpIndex = qmps.findIndex(qmp => qmp.id === qmpId)
      const qmp = state.qmps[qmpIndex]
      const itemIndex = qmp.items.findIndex(item => item.sellerId === sellerId && item.historyId === historyId)
      qmp.items.splice(itemIndex, 1)
      qmps[qmpIndex] = qmp
      commit('setQmps', qmps)
    })
    .catch(err => {
      logger.error(err);
      notifyError(commit, 'No pudimos eliminar el QMI del QMP')
    })
    .then(() => commit('setLoadingRemoveQmiFromQmp', true))
}

/**
 * Remove an entire QMP by its id.
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} payload.qmpId
 */
function removeQmp({ commit }, { qmpId }) {
  if (!qmpId) throw new Error('Please provide the "qmpId"')

  commit('setLoadingRemoveQmp', true)
  return api.similar.removeQmp({ qmpId })
    .then(() => notifySuccess(commit, 'QMP eliminado correctamente'))
    .catch(err => {
      logger.error(err);
      notifyError(commit, 'No pudimos eliminar el QMP')
    })
    .then(() => commit('setLoadingRemoveQmp', false))
}


/**
 * Set the app setting history ids and get again the pivots because that action
 * use this setting.
 *
 * @param {Object} context
 * @param {String[]} ids
 */
function setAppSettingsHistoryIds({ commit, dispatch }, ids) {
  commit('setAppSettingsHistoryIds', ids)
  return dispatch('getAllDataAccordingToRoute')
}

function setAppSettingsMatchingBrands({ commit, dispatch }, ids) {
  commit('setAppSettingsMatchingBrands', ids)
  return dispatch('getAllDataAccordingToRoute')
}


/**
 * Set the date to filter the items by `LastPriceUpdate` and get again all the data
 * that depends on this.
 *
 * @param {Object} context
 * @param {String} date with format YYYY-MM-DD
 */
async function setAppSettingsMinLastPriceUpdate({ commit, dispatch }, date) {
  commit('setAppSettingsMinLastPriceUpdate', date)
  return dispatch('getAllDataAccordingToRoute')
}


/**
 * Set the clients sellers ids setting in the app (to work only with items of these sellers)
 * and get again the classifications, brands, pivots and similar items if correspond.
 *
 * @param {Object} context
 * @param {Number[]} sellersIds
 */
async function setAppSettingsClientsSellersIds({ commit, dispatch }, sellersIds) {
  commit('setAppSettingsClientsSellersIds', sellersIds)
  return dispatch('getAllDataAccordingToRoute')
}


/**
 * Set the competitors sellers ids setting in the app (to work only with items of these sellers)
 * and get again the classifications, brands, pivots and similar items if correspond.
 *
 * @param {Object} context
 * @param {Number[]} sellersIds
 */
async function setAppSettingsCompetitorsSellersIds({ commit, dispatch }, sellersIds) {
  commit('setAppSettingsCompetitorsSellersIds', sellersIds)
  return dispatch('getAllDataAccordingToRoute')
}


/**
 * Set the brands pagination and dispatch the action to get the brands.
 * @param {Object} context
 * @param {Object} payload
 */
function setBrandsPagination({ commit, dispatch }, { classificationId, pagination }) {
  commit('setBrandsPagination', pagination)
  dispatch('getBrands', { classificationId })
}


/**
 * A simple action to set the pagination of the classifications and dispatch
 * the action to get the classifications page.
 * @param {Object} context
 * @param {Object} pagination
 */
function setClassificationsPagination({ commit, dispatch }, pagination) {
  commit('setClassificationsPagination', pagination)
  dispatch('getClassifications')
}

async function setSellerHomologations({ commit }) {
  try {
    const sellerHomologations = await api.global.sellerHomologations.getAll()
    commit('setSellerHomologations', sellerHomologations)
  } catch (error) {
    logger.error(error)
  }

}


export default {
  createIndex,
  createProduct,
  createProductWithSingleItem,
  getAllDataAccordingToRoute,
  getBrands,
  getClassifications,
  getPivotItems,
  getQmps,
  getSimilarItems,
  getSimilarItemsQmps,
  removeQmiFromQmp,
  removeQmp,
  setAppSettingsClientsSellersIds,
  setAppSettingsCompetitorsSellersIds,
  setAppSettingsHistoryIds,
  setAppSettingsMatchingBrands,
  setAppSettingsMinLastPriceUpdate,
  setBrandsPagination,
  setClassificationsPagination,
  getMatchRanking,
  setSellerHomologations
}
